/**
 * PR3.6A — read-only, month-to-date + remaining PUBLISHED roster projection.
 * No salaries, tax, snapshots, financial approvals, or data writes occur here.
 * All dates are YYYY-MM-DD calendar dates from the employer's selected payroll month.
 */
const round2 = value => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
const HOUR_FIELDS = ['monSatHrs', 'sundayHours', 'publicHours', 'overtimeHrs', 'paidLeaveHrs'];
const validDay = value => /^\d{4}-\d{2}-\d{2}$/.test(String(value || '')) &&
  !Number.isNaN(Date.parse(`${value}T12:00:00Z`)) && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;
const add = (target, source) => {
  HOUR_FIELDS.forEach(field => { target[field] = round2(target[field] + (Number(source[field]) || 0)); });
  target.totalHrs = round2(HOUR_FIELDS.reduce((sum, field) => sum + target[field], 0));
};
const newRow = (entry) => ({
  key: entry.key, name: entry.name, staffId: entry.staffId, storeId: entry.storeId,
  monSatHrs: 0, sundayHours: 0, publicHours: 0, overtimeHrs: 0, paidLeaveHrs: 0,
  totalHrs: 0, reviewReasons: [],
});
const scheduledHours = entry => {
  const shift = String(entry.publishedShift || '').trim();
  const match = /^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/.exec(shift);
  if (!match) return null;
  const h1 = Number(match[1]); const m1 = Number(match[2]);
  const h2 = Number(match[3]); const m2 = Number(match[4]);
  if (h1 > 23 || h2 > 23 || m1 > 59 || m2 > 59) return null;
  let grossMinutes = h2 * 60 + m2 - h1 * 60 - m1;
  if (grossMinutes <= 0) grossMinutes += 1440;
  const lunch = Number.isFinite(Number(entry.lunchBreakMinutes)) && entry.lunchBreakMinutes !== null && entry.lunchBreakMinutes !== ''
    ? Number(entry.lunchBreakMinutes) : Number(entry.defaultLunchMin || 0);
  const computed = round2(Math.max(0, grossMinutes / 60 - Math.max(0, lunch) / 60));
  const savedNet = Number(entry.scheduledNetHours);
  // The schedule builder stores net hours. Prefer them when valid and plausible.
  const payable = entry.scheduledNetHours !== null && entry.scheduledNetHours !== undefined && entry.scheduledNetHours !== '' &&
    Number.isFinite(savedNet) && savedNet >= 0 && savedNet <= grossMinutes / 60
    ? round2(savedNet) : computed;
  return { hours: payable, overnight: (h2 * 60 + m2) <= (h1 * 60 + m1), grossMinutes, computed };
};

export function buildPayrollForecast({ acceptedDays = [], publishedEntries = [], periodStart, periodEnd, cutoff,
  selectedStoreId = 'ALL', allowedStoreIds = [], publicHolidays = [], publicPayEnabled = true, defaultLunchMin = 60 }) {
  if (![periodStart, periodEnd, cutoff].every(validDay) || periodStart > cutoff || cutoff > periodEnd ||
    periodStart.slice(0, 7) !== periodEnd.slice(0, 7) || periodStart.slice(8) !== '01' ||
    periodEnd !== new Date(Date.UTC(Number(periodStart.slice(0,4)), Number(periodStart.slice(5,7)), 0)).toISOString().slice(0,10)) {
    return { valid: false, reason: 'Choose the full calendar month (1st through last day) and an actual-through date inside that month.' };
  }
  const allowed = new Set(allowedStoreIds.map(String));
  const inScope = id => selectedStoreId === 'ALL' ? allowed.has(String(id)) : String(id) === String(selectedStoreId) && allowed.has(String(id));
  const holidaySet = new Set(Array.from(publicHolidays));
  const actual = new Map();
  const projected = new Map();
  const warnings = new Set();
  const forecastShifts = [];
  let actualEvidenceDays = 0;
  const actualDailyEvidence = [];
  let excludedPostCutoffActual = 0;
  acceptedDays.forEach(day => {
    if (!inScope(day.storeId) || !validDay(day.date) || day.date < periodStart || day.date > periodEnd) return;
    if (day.date > cutoff) { excludedPostCutoffActual += 1; return; }
    if (!actual.has(day.key)) actual.set(day.key, newRow(day));
    const row = actual.get(day.key);
    add(row, day);
    (day.reviewReasons || []).forEach(reason => { if (!row.reviewReasons.includes(reason)) row.reviewReasons.push(reason); });
    actualEvidenceDays += 1;
    actualDailyEvidence.push({key:day.key,staffId:String(day.staffId||''),storeId:String(day.storeId||''),date:day.date,
      monSatHrs:Number(day.monSatHrs)||0,sundayHours:Number(day.sundayHours)||0,publicHours:Number(day.publicHours)||0,
      overtimeHrs:Number(day.overtimeHrs)||0,paidLeaveHrs:Number(day.paidLeaveHrs)||0,
      sourcePath:String(day.sourcePath||'')});
  });
  const seen = new Set();
  publishedEntries.forEach(entry => {
    if (entry.published !== true || !validDay(entry.date) || entry.date <= cutoff || entry.date > periodEnd || !inScope(entry.storeId)) return;
    const storeId = String(entry.storeId);
    const staffId = String(entry.staffId || '').trim();
    const name = String(entry.staffName || 'Staff member').trim();
    if (!staffId) { warnings.add('Some published roster entries have no stable staff ID; those shifts were excluded.'); return; }
    const key = `${storeId}__${staffId}`;
    const dayKey = `${key}|${entry.date}`;
    if (seen.has(dayKey)) { warnings.add('Duplicate published roster entries were found for a staff member on the same date; duplicates were excluded.'); return; }
    seen.add(dayKey);
    const shift = String(entry.publishedShift || '').trim();
    if (!shift || shift.toUpperCase() === 'OFF') return;
    const bucket = {monSatHrs:0,sundayHours:0,publicHours:0,overtimeHrs:0,paidLeaveHrs:0};
    let overnight = false;
    let kind = 'shift';
    if (shift.startsWith('ATTENDANCE::') || entry.isAttendance === true) {
      if (entry.attendancePaid !== true || entry.attendanceAllocateHours !== true) return;
      const hours = Number(entry.attendanceHours);
      if (!Number.isFinite(hours) || hours <= 0 || hours > 24) { warnings.add('One or more paid attendance entries have missing or invalid allocated hours.'); return; }
      bucket.paidLeaveHrs = round2(hours);
      kind = 'paid attendance';
    } else {
      const span = scheduledHours({ ...entry, defaultLunchMin });
      if (!span) { warnings.add('Some published shifts could not be parsed and were excluded.'); return; }
      overnight = span.overnight;
      if (overnight) warnings.add('Overnight forecast shifts require manual review of Sunday/public-holiday allocation.');
      const sunday = new Date(`${entry.date}T12:00:00Z`).getUTCDay() === 0;
      const publicDay = publicPayEnabled && holidaySet.has(entry.date);
      if (sunday && publicDay) warnings.add('A Sunday is also marked as a public holiday; confirm the applicable premium before finalisation.');
      if (sunday) bucket.sundayHours = span.hours;
      else if (publicDay) bucket.publicHours = span.hours;
      else bucket.monSatHrs = span.hours;
    }
    if (!projected.has(key)) projected.set(key, newRow({key, name, staffId, storeId}));
    add(projected.get(key), bucket);
    forecastShifts.push({ key, name, staffId, storeId, date: entry.date, shift, hours: round2(HOUR_FIELDS.reduce((n, f) => n + bucket[f], 0)),
      kind, overnight, ...bucket });
  });
  if (!forecastShifts.length && cutoff < periodEnd) warnings.add('No paid published shifts/leave were found after the cutoff. Check that the remaining roster was published.');
  if (excludedPostCutoffActual) warnings.add('Actual shifts recorded after the selected cutoff are intentionally excluded and replaced by published schedule forecasts.');
  const keys = Array.from(new Set([...actual.keys(), ...projected.keys()]));
  const forecastCountByKey = new Map();
  forecastShifts.forEach(shift => forecastCountByKey.set(shift.key,(forecastCountByKey.get(shift.key)||0)+1));
  const rows = keys.map(key => {
    const a = actual.get(key) || newRow(projected.get(key));
    const p = projected.get(key) || newRow(actual.get(key));
    const combined = newRow(a);
    add(combined, a); add(combined, p);
    return { ...combined, actual: a, projected: p, forecastShiftCount: forecastCountByKey.get(key)||0,
      reviewReasons: [...a.reviewReasons, ...p.reviewReasons] };
  }).sort((a,b) => a.storeId.localeCompare(b.storeId) || a.name.localeCompare(b.name));
  const sum = data => data.reduce((acc, row) => { add(acc, row); return acc; }, {monSatHrs:0,sundayHours:0,publicHours:0,overtimeHrs:0,paidLeaveHrs:0,totalHrs:0});
  return { valid:true, cutoff, periodStart, periodEnd, rows, forecastShifts, actual:sum(Array.from(actual.values())),
    projected:sum(Array.from(projected.values())), combined:sum(rows), actualEvidenceDays, excludedPostCutoffActual,
    warnings:Array.from(warnings), actualDailyEvidence };
}
